import React, { useContext, useState } from 'react';
import { Query } from 'react-apollo';
import { Container } from '@sm/wds-react';
import { BasePage, Helmet, StaticContext, SkeletonLoader } from '@sm/webassets';
import { t, defineMessages } from '@sm/intl';

import theme from '../../styles/SurveyMonkey.theme';
import GetCollectorByIdQuery from '~app/pages/SurveyClosed/GetCollectorById.graphql';

import BrandedSurveyClosed from '~app/pages/SurveyClosed/BrandedSurveyClosed';
import EUBrandedSurveyClosed from '~app/pages/SurveyClosed/EUBrandedSurveyClosed';
import NonBrandedSurveyClosed from '~app/pages/SurveyClosed/NonBrandedSurveyClosed';

const COPY = defineMessages({
  DEFAULT_CLOSED_MESSAGE: {
    id: 'SurveyClosed.closedMessage',
    defaultMessage:
      'This survey is currently closed.  Please contact the author of this survey for further assistance.',
  },
  META_KEYWORDS: {
    id: 'SurveyClosed.metaKeywords',
    defaultMessage:
      'questionnaire, questionnaires, questionaire, questionaires, free online survey, free online surveys',
  },
  CLOSED_PAGE_TITLE: {
    id: 'SurveyClosed.title',
    defaultMessage: 'Free Online Survey Software by SurveyMonkey: Closed Survey',
  },
  CLOSED_PAGE_NON_BRANDED_TITLE: {
    id: 'SurveyClosed.nonBrandedTitle',
    defaultMessage: 'Online Survey Software: Closed Survey',
  },
});

const SurveyClosed = () => {
  const {
    pageOptions: { canonical, collectorId, isNonBranded = false, languageId: surveyLangId },
    locale,
  } = useContext(StaticContext);
  const [showFooter, setShowFooter] = useState(true);

  return (
    <BasePage includeHeader={false} includeFooter={showFooter} pageId="Survey Closed" legacyWeb="anonweb" theme={theme}>
      <Helmet>
        <base target="_top" href="/" />
        <meta name="keywords" content={t(COPY.META_KEYWORDS)} />
        <meta name="description" content={t(COPY.DEFAULT_CLOSED_MESSAGE)} />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <Query
        context={{
          headers: {
            'X-Collector-Id': collectorId,
            'X-Language-Id': surveyLangId,
          },
          targetPath: '/lo-graphql/collector',
        }}
        query={GetCollectorByIdQuery}
        variables={{ collectorId, surveyLangId }}
        errorPolicy="all"
      >
        {({ loading, data, error }) => {
          if (data) {
            const isCollectorBrandingDisabled = data.collector.closedPage.isBrandingDisabled;
            const isContextBrandingDisabled = isNonBranded;
            if (isCollectorBrandingDisabled || isContextBrandingDisabled) {
              setShowFooter(false);
            }
          }
          if (error) {
            setShowFooter(false);
            return (
              <>
                <Helmet>
                  <title>{t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE)}</title>
                </Helmet>
                <NonBrandedSurveyClosed message={t(COPY.DEFAULT_CLOSED_MESSAGE)} />;
              </>
            );
          }
          return (
            <>
              {loading && (
                <Container flex flexJustify="center" flexAlign="center" p="5">
                  <SkeletonLoader height={130} width={400}>
                    <circle cx="10" cy="20" r="8" />
                    <rect x="25" y="15" rx="5" ry="5" width="350" height="10" />
                  </SkeletonLoader>
                </Container>
              )}
              {data && (
                <Choose>
                  <When condition={isNonBranded || data.collector.closedPage.isBrandingDisabled}>
                    <Helmet>
                      <title>{t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE)}</title>
                    </Helmet>
                    <NonBrandedSurveyClosed
                      message={
                        (data.collector && data.collector.closedPage.closedMessage) || t(COPY.DEFAULT_CLOSED_MESSAGE)
                      }
                    />
                  </When>
                  <When condition={locale.isEUSubdomain}>
                    <Helmet>
                      <title>{t(COPY.CLOSED_PAGE_NON_BRANDED_TITLE)}</title>
                    </Helmet>
                    <EUBrandedSurveyClosed
                      message={
                        (data.collector && data.collector.closedPage.closedMessage) || t(COPY.DEFAULT_CLOSED_MESSAGE)
                      }
                    />
                  </When>
                  <Otherwise>
                    <Helmet>
                      <title>{t(COPY.CLOSED_PAGE_TITLE)}</title>
                    </Helmet>
                    <BrandedSurveyClosed
                      message={
                        (data.collector && data.collector.closedPage.closedMessage) || t(COPY.DEFAULT_CLOSED_MESSAGE)
                      }
                    />
                  </Otherwise>
                </Choose>
              )}
            </>
          );
        }}
      </Query>
    </BasePage>
  );
};

export default SurveyClosed;
